import {
  InteractionRequiredAuthError,
  type AccountInfo
} from "@azure/msal-browser";
import { toast } from "react-toastify";

import {
  bffLoginRequest,
  loginRequest,
  msalInstance
} from "../config/msal-config";
import { TOKEN_ERROR } from "../constants/error";

import { markSharedLogout } from "./authSync";
import storage from "./storage";

/**
 * MSAL is the single source of truth for access tokens.
 *
 * `acquireTokenSilent` reads MSAL's own localStorage cache and returns the
 * stored token with no network call, performing the refresh-token grant only
 * when the cached token is missing or within `tokenRenewalOffsetSeconds`
 * (300s by default) of expiring. This module used to mirror tokens into
 * `storage` behind a 60s expiry check of its own, which bought nothing (the
 * call it skipped was a localStorage read, not a round-trip) and created a
 * second copy that nothing invalidated on a 401 — so a rejected-but-unexpired
 * token stayed pinned for its whole remaining lifetime, and `forceRefresh`
 * could never take effect because the mirror answered first.
 */

/** The token audiences this app requests; each maps to one MSAL scope set. */
export type TokenAudience = "graph" | "bff";

const SCOPES: Record<TokenAudience, string[]> = {
  graph: loginRequest.scopes,
  // Azure AD allows one resource per token, so the BFF's own app-registration
  // scope is requested separately from the Graph scopes above.
  bff: bffLoginRequest.scopes
};

type PendingAcquisition = {
  promise: Promise<string>;
  forceRefresh: boolean;
};

/**
 * Concurrent callers share one acquisition per audience, so a burst of parallel
 * requests refreshing at the same time triggers a single token request.
 */
const inFlight = new Map<TokenAudience, PendingAcquisition>();

const getAccount = (): AccountInfo | undefined =>
  msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

/** Signs the user out; the session can no longer produce a usable token. */
const failSession = (error: unknown): never => {
  toast.error(TOKEN_ERROR);
  logOut();
  throw error;
};

const acquireTokenInteractively = async (
  audience: TokenAudience
): Promise<string> => {
  try {
    const { accessToken } = await msalInstance.acquireTokenPopup({
      scopes: SCOPES[audience]
    });
    return accessToken;
  } catch (error) {
    // Popup blocked or dismissed — nothing left to try.
    return failSession(error);
  }
};

const acquireToken = async (
  audience: TokenAudience,
  forceRefresh: boolean
): Promise<string> => {
  try {
    const { accessToken } = await msalInstance.acquireTokenSilent({
      scopes: SCOPES[audience],
      account: getAccount(),
      forceRefresh
    });
    return accessToken;
  } catch (error) {
    // The refresh token itself needs the user (expiry, CA policy, new consent):
    // prompt instead of signing them out.
    if (error instanceof InteractionRequiredAuthError) {
      return acquireTokenInteractively(audience);
    }
    return failSession(error);
  }
};

/**
 * Returns an access token for `audience`, refreshing it when MSAL's cached copy
 * is expired or near expiry.
 *
 * Pass `forceRefresh` only after an API has *rejected* a token (401/403) — it
 * bypasses the cache so the replay sends a genuinely new token rather than the
 * one just refused. See the response interceptors in `src/lib/axios.ts`.
 */
export const getToken = async (
  audience: TokenAudience,
  { forceRefresh = false }: { forceRefresh?: boolean } = {}
): Promise<string> => {
  const pending = inFlight.get(audience);

  // Join an in-flight acquisition, unless it would resolve with a token we
  // already know to be stale (a cached read while we need a forced refresh).
  if (pending && (pending.forceRefresh || !forceRefresh)) {
    return pending.promise;
  }

  const promise = acquireToken(audience, forceRefresh).finally(() => {
    if (inFlight.get(audience)?.promise === promise) {
      inFlight.delete(audience);
    }
  });

  inFlight.set(audience, { promise, forceRefresh });
  return promise;
};

/**
 * Microsoft Graph token (`User.Read`), used only to read the signed-in user's
 * identity claims. SharePoint goes through the BFF, which does its own
 * on-behalf-of exchange — nothing here calls Graph for files.
 */
export const getMslToken = () => getToken("graph");

/**
 * Access token whose `aud` is our own app registration rather than Microsoft
 * Graph, so the BFF can cryptographically verify the signature.
 */
export const getBffToken = () => getToken("bff");

export const logOut = () => {
  // Signal and clear before redirecting: logoutRedirect() navigates the page
  // away, so anything sequenced after it is racing the navigation.
  markSharedLogout();
  inFlight.clear();
  // logoutRedirect clears MSAL's own token cache as part of signing out.
  void msalInstance.logoutRedirect();
};

// Evicts the token copies earlier builds mirrored into localStorage. Safe to
// delete once every browser has loaded a build containing this line.
storage.clearLegacyTokens();
